import React, { FC, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RegionFormSelectDrawer } from './RegionFormSelectDrawer';
import { Select } from '@/components/UI/Select/Select';
import { AdFormFields } from '@/components/AdForm/AdForm.types';
import { Region } from '@/api/types/regions.types';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { NotFound } from '@/components/NotFoundPlaceholder/NotFoundPlaceholder.styles';
import {
  SelectDropdownMenu,
  SelectDropdownMenuItem,
} from '@/components/UI/Dropdown/SelectDropdownMenu.styles';
import useScreenSize from '@/hooks/useScreenSize';
import { SelectRef } from '@/components/UI/Select/Select.types';

interface Props {
  regions?: Region[];
}

export const RegionFormSelect: FC<Props> = ({ regions }) => {
  const { control } = useFormContext<AdFormFields>();
  const {
    field: { name, value, onChange },
    fieldState,
  } = useController({ name: 'regionId', control });
  const { t } = useTranslation();
  const [isOpen, toggle] = useToggleOpenState();
  const [query, setQuery] = useState('');
  const selectRef = useRef<SelectRef>(null);
  const { isLarge } = useScreenSize();

  const list =
    regions?.filter(region =>
      region.name.toLowerCase().includes(query.toLowerCase())
    ) || [];

  const visibleValue = regions?.find(region => region.id === value)?.name;

  function onSelect(region: Region) {
    onChange(region.id);
    toggle(false);
    selectRef.current?.clearSearchValue();
  }

  if (!regions) return null;

  return (
    <Select
      id="lot_region_id"
      name={name}
      isOpen={isOpen}
      toggle={toggle}
      ref={selectRef}
      value={value}
      label={t('common.city')}
      visibleValue={visibleValue}
      hasSearch={isLarge}
      hasDropdown={isLarge}
      onSearch={setQuery}
      isRequired
      helperText={fieldState.error?.message}
      hasError={!!fieldState.error}
      appearance="filled"
    >
      {isLarge ? (
        isOpen && (
          <SelectDropdownMenu $hasPadding>
            {list.length > 0 ? (
              list.map(region => {
                return (
                  <SelectDropdownMenuItem
                    key={region.id}
                    onChangeHook={() => onSelect(region)}
                    hasBorderBottom={false}
                    value={region.id === value}
                    label={region.name}
                    highlightLabel
                    appearance="radio"
                    size="sm"
                  />
                );
              })
            ) : (
              <NotFound>{t('common.nothing_found')}</NotFound>
            )}
          </SelectDropdownMenu>
        )
      ) : (
        <RegionFormSelectDrawer
          isOpen={isOpen}
          close={() => toggle(false)}
          setQuery={setQuery}
          list={list}
          value={value}
          onSelect={onSelect}
        />
      )}
    </Select>
  );
};
